// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Invoice from "./invoice.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "invoice_id": {
      "column": {
        "default": null,
        "name": "invoice_id",
        "null": false,
        "type": null
      },
      "name": "invoice_id",
      "selected_by_default": null,
      "translated": null
    },
    "price": {
      "column": null,
      "name": "price",
      "selected_by_default": null,
      "translated": null
    },
    "price_cents": {
      "column": {
        "default": "0",
        "name": "price_cents",
        "null": false,
        "type": "integer"
      },
      "name": "price_cents",
      "selected_by_default": null,
      "translated": null
    },
    "price_currency": {
      "column": {
        "default": "USD",
        "name": "price_currency",
        "null": false,
        "type": "string"
      },
      "name": "price_currency",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    },
    "vat": {
      "column": null,
      "name": "vat",
      "selected_by_default": null,
      "translated": null
    },
    "vat_cents": {
      "column": {
        "default": "0",
        "name": "vat_cents",
        "null": false,
        "type": "integer"
      },
      "name": "vat_cents",
      "selected_by_default": null,
      "translated": null
    },
    "vat_currency": {
      "column": {
        "default": "USD",
        "name": "vat_currency",
        "null": false,
        "type": "string"
      },
      "name": "vat_currency",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "InvoiceLine",
  "collectionKey": "invoice_lines",
  "collectionName": "invoice_lines",
  "i18nKey": "invoice_line",
  "camelizedLower": "invoiceLine",
  "name": "InvoiceLine",
  "nameDasherized": "invoice-line",
  "pluralName": "invoice_lines",
  "ransackable_associations": [
    {
      "className": "InvoiceLine::Audit",
      "collectionName": "invoice_line_audits",
      "foreignKey": "invoice_line_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "InvoiceLineAudit",
      "through": null
    },
    {
      "className": "InvoiceLine::Audit",
      "collectionName": "invoice_line_audits",
      "foreignKey": "invoice_line_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "InvoiceLineAudit",
      "through": null
    },
    {
      "className": "Invoice",
      "collectionName": "invoices",
      "foreignKey": "invoice_id",
      "name": "invoice",
      "macro": "belongs_to",
      "resource_name": "Invoice",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "invoice_id",
      "column": {
        "default": null,
        "name": "invoice_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "price_cents",
      "column": {
        "default": "0",
        "name": "price_cents",
        "null": false,
        "type": "integer"
      }
    },
    {
      "name": "price_currency",
      "column": {
        "default": "USD",
        "name": "price_currency",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "vat_cents",
      "column": {
        "default": "0",
        "name": "vat_cents",
        "null": false,
        "type": "integer"
      }
    },
    {
      "name": "vat_currency",
      "column": {
        "default": "USD",
        "name": "vat_currency",
        "null": false,
        "type": "string"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "Invoice",
      "collectionName": "invoices",
      "foreignKey": "invoice_id",
      "name": "invoice",
      "macro": "belongs_to",
      "resource_name": "Invoice",
      "through": null
    }
  ],
  "paramKey": "invoice_line",
  "primaryKey": "id"
}

/** Frontend model for InvoiceLine. */
export default class InvoiceLine extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  invoiceId() {
    return this.readAttributeUnderscore("invoice_id")
  }

  /** @returns {boolean} */
  hasInvoiceId() {
    const value = this.invoiceId()

    return this._isPresent(value)
  }

  /** @returns {any} */
  price() {
    return this.readAttributeUnderscore("price")
  }

  /** @returns {boolean} */
  hasPrice() {
    const value = this.price()

    return this._isPresent(value)
  }

  /** @returns {number} */
  priceCents() {
    return this.readAttributeUnderscore("price_cents")
  }

  /** @returns {boolean} */
  hasPriceCents() {
    const value = this.priceCents()

    return this._isPresent(value)
  }

  /** @returns {string} */
  priceCurrency() {
    return this.readAttributeUnderscore("price_currency")
  }

  /** @returns {boolean} */
  hasPriceCurrency() {
    const value = this.priceCurrency()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  vat() {
    return this.readAttributeUnderscore("vat")
  }

  /** @returns {boolean} */
  hasVat() {
    const value = this.vat()

    return this._isPresent(value)
  }

  /** @returns {number} */
  vatCents() {
    return this.readAttributeUnderscore("vat_cents")
  }

  /** @returns {boolean} */
  hasVatCents() {
    const value = this.vatCents()

    return this._isPresent(value)
  }

  /** @returns {string} */
  vatCurrency() {
    return this.readAttributeUnderscore("vat_currency")
  }

  /** @returns {boolean} */
  hasVatCurrency() {
    const value = this.vatCurrency()

    return this._isPresent(value)
  }

  /** @returns {import("./invoice.js").default | null} */
  invoice() {
    return this._readBelongsToReflection({modelClass: Invoice, reflectionName: "invoice"})
  }

  /** @returns {Promise<import("./invoice.js").default | null>} */
  loadInvoice() {
    if (!("invoiceId" in this)) throw new Error("Foreign key method wasn't defined: invoiceId")

    const id = this.invoiceId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "invoice",
        model: this,
        modelClass: Invoice
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./invoice.js").default | null>} */
  ensureInvoiceLoaded() {
    return this.ensureAssociationLoaded("invoice")
  }
}
